import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';
import { DD_MM_YYYY_DATE_FORMAT } from '@core/utils/utils';

export interface CancelTaskChoiceDialogData {
  taskTitle: string;
  /** Day currently selected in the week grid; used as the default for the date picker. */
  selectedDate?: Date;
  /** The task definition's start date; used as the minimum selectable date. */
  taskStartDate?: Date;
}

export enum CancelTaskChoice {
  SELECTED_DATE = 'SELECTED_DATE',
  ALL_FUTURE = 'ALL_FUTURE',
  CANCEL = 'CANCEL'
}

/** How far an "all future" cancellation should reach. */
export enum AllFutureScope {
  /** Cancel every occurrence, from the definition's start. */
  ALL = 'ALL',
  /** Cancel occurrences from a chosen date onward; earlier tasks stay visible. */
  FROM_DATE = 'FROM_DATE'
}

export interface CancelTaskChoiceResult {
  choice: CancelTaskChoice;
  /** Set only when choice is ALL_FUTURE. */
  scope?: AllFutureScope;
  /** Set only when choice is ALL_FUTURE and scope is FROM_DATE. */
  fromDate?: Date;
}

@Component({
  selector: "app-cancel-task-choice-dialog",
  templateUrl: "./cancel-task-choice-dialog.component.html",
  styleUrl: "./cancel-task-choice-dialog.component.scss",
  standalone: false,
  // The adapter is re-provided here so it picks up this locale: the root one is built with
  // the app's default and would keep rendering M/D/YYYY.
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: DD_MM_YYYY_DATE_FORMAT }
  ]
})
export class CancelTaskChoiceDialogComponent {
  CancelTaskChoice = CancelTaskChoice;
  AllFutureScope = AllFutureScope;

  /** 1 = pick selected-date vs all-future; 2 = pick the all-future scope. */
  step: 1 | 2 = 1;
  scope: AllFutureScope = AllFutureScope.ALL;
  fromDate: Date | null;
  minDate?: Date;

  constructor(
    public dialogRef: MatDialogRef<CancelTaskChoiceDialogComponent, CancelTaskChoiceResult>,
    @Inject(MAT_DIALOG_DATA) public data: CancelTaskChoiceDialogData
  ) {
    this.fromDate = data.selectedDate ?? null;
    this.minDate = data.taskStartDate;
  }

  selectSelectedDate(): void {
    this.dialogRef.close({ choice: CancelTaskChoice.SELECTED_DATE });
  }

  goToAllFutureStep(): void {
    this.step = 2;
  }

  backToChoices(): void {
    this.step = 1;
  }

  get isConfirmDisabled(): boolean {
    return this.scope === AllFutureScope.FROM_DATE && !this.fromDate;
  }

  confirmAllFuture(): void {
    this.dialogRef.close({
      choice: CancelTaskChoice.ALL_FUTURE,
      scope: this.scope,
      fromDate: this.scope === AllFutureScope.FROM_DATE ? (this.fromDate ?? undefined) : undefined
    });
  }

  onCancel(): void {
    this.dialogRef.close({ choice: CancelTaskChoice.CANCEL });
  }
}
